import { FC, useEffect, useState } from 'react';

// Externals
import firebase from 'firebase';
import { Formik, FormikHelpers, FormikState } from 'formik';
import _ from 'lodash';
import { useSnackbar } from 'notistack';
import { useFirestoreDocData, useFirestore } from 'reactfire';
import * as yup from 'yup';

// Material
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core';

// Models
import { StationDTO } from '../../models/station-v3.dto';
import { StationType, getStationTypeLabel, StationTypes } from 'app/models/station.type';
import config from '../config';

// Styles
const useStyles = makeStyles((theme) => ({
  content: {
    paddingTop: '0 !important'
  },
  title: {
    padding: theme.spacing(2, 3, 1)
  },
  linearProgress: {
    marginBottom: theme.spacing(1.5)
  }
}));

interface Props {
  id: string;
  open: boolean;
  onClose: () => void;
}

interface States {
  name: string;
  latitude: number;
  longitude: number;
  type: string;
}

const EditModal: FC<Props> = ({ id, open, onClose }) => {
  const classes = useStyles();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const stationRef = useFirestore().collection(config.dataCollection).doc(id);

  const { status, data, hasEmitted } = useFirestoreDocData<StationDTO>(stationRef, {
    initialData: {
      name: '',
      coordinates: {
        latitude: 0,
        longitude: 0
      },
      type: ''
    }
  });

  const [initialValues, setInitialValues] = useState<States>({
    name: '',
    latitude: 0,
    longitude: 0,
    type: ''
  });

  const onReset = (resetForm: (nextState?: Partial<FormikState<States>> | undefined) => void) => {
    resetForm();

    onClose();
  };

  const onSubmit = async (values: States, actions: FormikHelpers<States>) => {
    try {
      const station: StationDTO = {
        id: id,
        coordinates: new firebase.firestore.GeoPoint(+values.latitude, +values.longitude),
        latitude: values.latitude.toString(),
        longitude: values.longitude.toString(),
        name: values.name,
        type: values.type as StationType
      };

      await stationRef.set(station, { merge: true });

      const key = enqueueSnackbar(`La station de mesure a été modifiée avec succès !`, {
        variant: 'success',
        onClick: () => {
          closeSnackbar(key);
        }
      });

      onReset(actions.resetForm);
    } catch (error) {
      console.error(error);

      const key = enqueueSnackbar(
        `La station de mesure n'a pu être modifiée. Si le problème persiste, veuillez contacter votre support technique.`,
        {
          variant: 'error',
          onClick: () => {
            closeSnackbar(key);
          }
        }
      );
    }
  };

  useEffect(() => {
    if (!_.isEmpty(data))
      setInitialValues({
        name: data.name,
        latitude: data.coordinates.latitude,
        longitude: data.coordinates.longitude,
        type: data.type
      });
  }, [data]);

  return (
    <Formik
      initialValues={initialValues}
      isInitialValid={false}
      enableReinitialize={true}
      validateOnMount={true}
      validationSchema={yup.object().shape({
        name: yup.string().required(`Le nom est nécessaire.`),
        latitude: yup
          .number()
          .min(-90, `La latitude ne peut pas être inférieur à 90°.`)
          .min(-90, `La latitude ne peut pas être supérieur à 90°.`)
          .required(`La latitude est nécessaire.`),
        longitude: yup
          .number()
          .min(-180, `La longitude ne peut pas être inférieur à 180°.`)
          .max(180, `La longitude ne peut pas être supérieur à 180°.`)
          .required(`La longitude est nécessaire.`),
        type: yup.string().required(`Le type est nécessaire.`)
      })}
      onSubmit={onSubmit}
    >
      {({ handleBlur, handleChange, handleSubmit, resetForm, errors, touched, values, isSubmitting, isValid }) => (
        <Dialog open={open} onClose={() => onReset(resetForm)}>
          <DialogTitle className={classes.title} disableTypography>
            <Typography variant="h3">Modification</Typography>
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent className={classes.content}>
              <DialogContentText>
                {status === 'loading' || !hasEmitted
                  ? 'Chargement...'
                  : 'Veuillez remplir les champs ci-dessous. Les champs marqués par un astérisque sont obligatoires.'}
              </DialogContentText>
              {(status === 'loading' || !hasEmitted || isSubmitting) && (
                <LinearProgress className={classes.linearProgress} color="secondary" />
              )}
              <TextField
                disabled={status === 'loading' || !hasEmitted || isSubmitting}
                error={Boolean(touched.name && errors.name)}
                fullWidth={true}
                helperText={touched.name && errors.name}
                label="Nom*"
                margin="dense"
                name="name"
                onBlur={handleBlur}
                onChange={handleChange}
                type="text"
                value={values.name}
                variant="outlined"
              />
              <TextField
                disabled={status === 'loading' || !hasEmitted || isSubmitting}
                error={Boolean(touched.latitude && errors.latitude)}
                fullWidth={true}
                helperText={touched.latitude && errors.latitude}
                label="Latitude*"
                margin="dense"
                name="latitude"
                onBlur={handleBlur}
                onChange={handleChange}
                type="number"
                value={values.latitude}
                variant="outlined"
              />
              <TextField
                disabled={status === 'loading' || !hasEmitted || isSubmitting}
                error={Boolean(touched.longitude && errors.longitude)}
                fullWidth={true}
                helperText={touched.longitude && errors.longitude}
                label="Longitude*"
                margin="dense"
                name="longitude"
                onBlur={handleBlur}
                onChange={handleChange}
                type="number"
                value={values.longitude}
                variant="outlined"
              />
              <TextField
                disabled={status === 'loading' || !hasEmitted || isSubmitting}
                error={Boolean(touched.type && errors.type)}
                fullWidth={true}
                helperText={touched.type && errors.type}
                label="Type*"
                margin="dense"
                name="type"
                onBlur={handleBlur}
                onChange={handleChange}
                select
                value={values.type}
                variant="outlined"
              >
                <MenuItem value={StationTypes.Citizen}>{getStationTypeLabel(StationTypes.Citizen)}</MenuItem>
                <MenuItem value={StationTypes.School}>{getStationTypeLabel(StationTypes.School)}</MenuItem>
                <MenuItem value={StationTypes.StationBE}>{getStationTypeLabel(StationTypes.StationBE)}</MenuItem>
                <MenuItem value={StationTypes.Hospital}>{getStationTypeLabel(StationTypes.Hospital)}</MenuItem>
                <MenuItem value={StationTypes.Other}>{getStationTypeLabel(StationTypes.Other)}</MenuItem>
              </TextField>
            </DialogContent>
            <DialogActions>
              <Button color="primary" onClick={() => onReset(resetForm)} type="button">
                Annuler
              </Button>
              <Button
                color="primary"
                disabled={status === 'loading' || !hasEmitted || isSubmitting || !isValid}
                type="submit"
                variant="contained"
              >
                Modifer
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      )}
    </Formik>
  );
};

export default EditModal;
